import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  ViewChild,
  ViewEncapsulation,
  EventEmitter,
  Output,
  HostListener,
  TemplateRef,
  Optional,
  SkipSelf,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { PHOENIX_CONTEXTS, PhxContextService } from '../../services/phx-context-service';
import { PhxBreakpointObserverService } from '../../services/phx-breakpoint-service';
import { CircleLoading } from './../icon/icon-map';
import { PhxBackground } from '../../types';
import mixinDisabled from '../../mixins/disabled';

export type PhxButtonSize = 'medium' | 'small' | 'large' | 'extra-small';
export type PhxButtonType = 'default' | 'positive' | 'negative' | 'neutral' | 'icon' | 'caution' | 'ghost' | 'inverted';
export type PhxButtonRole = 'primary' | 'secondary';

const baseButtonMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

@Component({
  selector: '[phx-button]',
  styleUrls: ['./phx-button.scss'],
  templateUrl: './phx-button.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: ['disabled'],
  host: {
    class: 'phx-button',
    '[class.phx-button-primary-default-standard]': `phxButtonRole === 'primary' && phxButtonType === 'default' && phxBackground === 'standard'`,
    '[class.phx-button-secondary-default-standard]': `phxButtonRole === 'secondary' && phxButtonType === 'default' && phxBackground === 'standard'`,
    '[class.phx-button-primary-ghost-standard]': `phxButtonRole === 'primary' && phxButtonType === 'ghost' && phxBackground === 'standard'`,
    '[class.phx-button-primary-inverted-standard]': `phxButtonRole === 'primary' && phxButtonType === 'inverted' && phxBackground === 'standard'`,
    '[class.phx-button-secondary-inverted-standard]': `phxButtonRole === 'secondary' && phxButtonType === 'inverted' && phxBackground === 'standard'`,
    '[class.phx-button-primary-default-bold]': `phxButtonRole === 'primary' && phxButtonType === 'default' && phxBackground === 'bold'`,
    '[class.phx-button-secondary-default-bold]': `phxButtonRole === 'secondary' && phxButtonType === 'default' && phxBackground === 'bold'`,
    '[class.phx-button-secondary-default-medium]': `phxButtonRole === 'secondary' && phxButtonType === 'default' && phxBackground === 'medium'`,
    '[class.phx-button-primary-default-overlay]': `phxButtonRole === 'primary' && phxButtonType === 'default' && phxBackground === 'overlay'`,
    '[class.phx-button-secondary-default-overlay]': `phxButtonRole === 'secondary' && phxButtonType === 'default' && phxBackground === 'overlay'`,
    '[class.phx-button-primary-default-inverted]': `phxButtonRole === 'primary' && phxButtonType === 'default' && phxBackground === 'inverted'`,
    '[class.phx-button-secondary-default-inverted]': `phxButtonRole === 'secondary' && phxButtonType === 'default' && phxBackground === 'inverted'`,
    '[class.phx-button-primary-default-summary]': `phxButtonRole === 'primary' && phxButtonType === 'default' && phxBackground === 'summary'`,
    '[class.phx-button-secondary-default-summary]': `phxButtonRole === 'secondary' && phxButtonType === 'default' && phxBackground === 'summary'`,
    '[class.phx-button-primary-default-dark]': `phxButtonRole === 'primary' && phxButtonType === 'default' && phxBackground === 'dark'`,
    '[class.phx-button-secondary-default-dark]': `phxButtonRole === 'secondary' && phxButtonType === 'default' && phxBackground === 'dark'`,
    '[class.phx-button-primary-positive-standard]': `phxButtonRole === 'primary' && phxButtonType === 'positive' && phxBackground === 'standard'`,
    '[class.phx-button-secondary-positive-standard]': `phxButtonRole === 'secondary' && phxButtonType === 'positive' && phxBackground === 'standard'`,
    '[class.phx-button-primary-negative-standard]': `phxButtonRole === 'primary' && phxButtonType === 'negative' && phxBackground === 'standard'`,
    '[class.phx-button-secondary-negative-standard]': `phxButtonRole === 'secondary' && phxButtonType === 'negative' && phxBackground === 'standard'`,
    '[class.phx-button-primary-neutral-standard]': `phxButtonRole === 'primary' && phxButtonType === 'neutral' && phxBackground === 'standard'`,
    '[class.phx-button-secondary-neutral-standard]': `phxButtonRole === 'secondary' && phxButtonType === 'neutral' && phxBackground === 'standard'`,
    '[class.phx-button-primary-caution-standard]': `phxButtonRole === 'primary' && phxButtonType === 'caution' && phxBackground === 'standard'`,
    '[class.phx-button-secondary-caution-standard]': `phxButtonRole === 'secondary' && phxButtonType === 'caution' && phxBackground === 'standard'`,
    '[class.phx-button-icon]': `phxButtonType === 'icon'`,
    '[class.phx-button-lg]': `phxButtonType !== 'icon' && phxSize === 'large'`,
    '[class.phx-button-md]': `phxButtonType !== 'icon' && phxSize === 'medium'`,
    '[class.phx-button-sm]': `phxButtonType !== 'icon' && phxSize === 'small'`,
    '[class.phx-button-extra-sm]': `phxButtonType !== 'icon' && phxSize === 'extra-small'`,
    '[class.phx-has-shadow]': `phxHasShadow === true`,
    '[class.phx-disabled]': `disabled === true`,
    '[class.phx-disable-cursor]': `phxLoading || phxDisableInteractions`,
    '[class.phx-full-width]': `phxFullWidth`,
    '[class.phx-filter]': 'phxStyleType === PHOENIX_CONTEXTS.FILTER',
    '[attr.tabindex]': '(disabled || phxLoading) ? -1 : null',
    '[attr.disabled]': 'disabled || phxLoading || phxDisableInteractions || null',
  },
})
export class PhxButtonComponent extends baseButtonMixin implements OnInit, OnDestroy {
  @Input() public phxSize: PhxButtonSize = 'medium';
  @Input() public phxBackground: PhxBackground = 'standard';
  @Input() public phxButtonType: PhxButtonType = 'default';
  @Input() public phxButtonRole: PhxButtonRole = 'primary';
  @Input() public phxLoading: boolean = false;
  @Input() public phxDisableInteractions: boolean = false;
  @Input() public phxSuffixIcon: string;
  @Input() public phxSuffixTemplate: TemplateRef<any>;
  @Input() public phxPrefixIcon: string;
  @Input() public phxPrefixTemplate: TemplateRef<any>;
  @Input() public phxHasShadow: boolean = false;
  @Input() public phxFullWidth: boolean = false;
  @Input() public phxOnBlue: boolean = false;
  @Output() public phxOnClick: EventEmitter<MouseEvent | KeyboardEvent> = new EventEmitter<MouseEvent | KeyboardEvent>();
  @ViewChild('buttonContent', { static: true }) public buttonContentRef: ElementRef<HTMLElement>;
  @HostListener('mouseup', ['$event'])
  @HostListener('keyup.space', ['$event'])
  @HostListener('keyup.enter', ['$event'])
  public handleClick(event: MouseEvent | KeyboardEvent): void {
    if (this.phxLoading) return;
    this.phxOnClick.emit(event);
  }

  public isContentEmpty = false;
  public phxStyleType: PHOENIX_CONTEXTS;
  public readonly circleLoadingIcon: string = CircleLoading;
  public readonly PHOENIX_CONTEXTS = PHOENIX_CONTEXTS;

  private _breakpointObserverSubscription: Subscription;

  constructor(private phxBreakpointObserver: PhxBreakpointObserverService, @Optional() @SkipSelf() contextService: PhxContextService) {
    super();
    this.phxStyleType = contextService?.getType();
  }

  ngOnInit(): void {
    if (this.phxOnBlue && this.phxBackground === 'inverted') {
      this._breakpointObserverSubscription = this.phxBreakpointObserver.smMobile.subscribe((matches: boolean) => {
        if (matches) {
          this.phxBackground = 'standard';
        } else {
          this.phxBackground = 'inverted';
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this._breakpointObserverSubscription) this._breakpointObserverSubscription.unsubscribe();
  }
}
